import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { useBulkAddItems } from '../hooks/use-item-mutations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCategories } from '@/features/categories/hooks/use-categories'

interface BulkAddByCategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  sectionId: number
}

export function BulkAddByCategoryDialog({
  open,
  onOpenChange,
  menuId,
  sectionId,
}: BulkAddByCategoryDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const bulkAddItems = useBulkAddItems()
  const { data: categoriesData } = useCategories()

  const handleBulkAdd = () => {
    if (!selectedCategory) return

    bulkAddItems.mutate(
      { menuId, sectionId, categoryCode: selectedCategory },
      {
        onSuccess: () => {
          onOpenChange(false)
          setSelectedCategory('')
          // Toast is shown by the mutation hook
        },
      },
    )
  }

  const categories = categoriesData?.items || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('bulkAdd.title')}</DialogTitle>
          <DialogDescription>{tMenus('bulkAdd.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {tMenus('bulkAdd.category')}
            </label>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger>
                <SelectValue placeholder={tMenus('bulkAdd.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.code} value={category.code}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCategory && (
            <div className="rounded-lg border p-3 bg-muted/50">
              <p className="text-sm text-muted-foreground">
                {tMenus('bulkAdd.hint')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={bulkAddItems.isPending}
          >
            {tMenus('bulkAdd.cancel')}
          </Button>
          <Button
            onClick={handleBulkAdd}
            disabled={bulkAddItems.isPending || !selectedCategory}
          >
            {bulkAddItems.isPending
              ? tMenus('bulkAdd.adding')
              : tMenus('bulkAdd.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
